import React from 'react';
import { useParams } from 'react-router-dom';
import ResidentHeaderContainer from '~/containers/ResidentDetail/ResidentHeader';
import dashboardWrapper from '~/wrappers/dashboard';
import ResidentLocationContainer from '~/containers/ResidentDetail/ResidentLocation';
import ResidentAlertsContainer from '~/containers/ResidentDetail/ResidentAlerts';
import ResidentSettingsContainer from '~/containers/ResidentDetail/ResidentSettings';
import connect from '~/store/connect';
import { State } from '~/store/types';
import { t } from '~/i18n';
import { TabsPanel, Spinner } from '@ixicare/ui';
import ErrorContainer from '~/containers/Error';
import BackLink from '~/components/BackLink';
import ResidentActivityContainer from '~containers/ResidentDetail/ResidentActivity';
import { Permission } from '@ixicare/resources';

interface ResidentDetailProps {
  loadResidentDetail: State['residentDetail']['loadResident'];
  residentLoading: State['residentDetail']['loading'];
  residentError: State['residentDetail']['error'];
  residentDetail: State['residentDetail'];
}

const ResidentDetail: React.FC<ResidentDetailProps> = ({
  loadResidentDetail,
  residentLoading,
  residentError,
  residentDetail,
}) => {
  const { id } = useParams<{ id: string }>();
  const residentId = id ? parseInt(id, 10) : 0;
  const [configurationPermission, setConfigurationPermission] = React.useState<boolean>();

  React.useEffect(() => {
    if (residentId) {
      loadResidentDetail(residentId.toString());
    }
  }, [residentId, loadResidentDetail]);

  React.useEffect(() => {
    Permission.getOwnPermissions().then((value) => {
      if (value.length != 0) {
        value[0].entries.find((perm) => {
          if (perm.permission.name.includes('can_edit_all_profiles')) {
            if (perm.isEnabled) {
              setConfigurationPermission(true);
            }
          }
        });
      }
    });
  }, []);

  // Set document title when profile loads
  React.useEffect(() => {
    if (residentDetail.profile) {
      document.title = t('meta.title', {
        title: residentDetail.profile.fullName,
      });
    }
  }, [residentDetail.profile]);

  const tabs = React.useMemo(() => {
    const data: any[] = [];
    if (residentDetail.profile?.ownedDevice) {
      data.push({
        label: t('residentDetail.tabs.activity'),
        id: 'activity',
        content: <ResidentActivityContainer residentId={residentId} />,
      });
    }
    data.push({
      label: t('residentDetail.tabs.alerts'),
      id: 'alerts',
      content: <ResidentAlertsContainer />,
    });
    if (residentDetail.profile?.ownedDevice) {
      data.push({
        label: t('residentDetail.tabs.settings'),
        id: 'settings',
        content: <ResidentSettingsContainer />,
      });
    }
    data.push({
      label: t('residentDetail.tabs.location'),
      id: 'location',
      content: <ResidentLocationContainer />,
    });
    return data;
  }, [residentDetail.profile, residentId]);

  if (residentLoading) {
    return <Spinner label={t('global.loading')} />;
  }

  return (
    <>
      <BackLink fallbackUrl="/residents" name={t('residentDetail.goBack')} />
      <ErrorContainer error={residentDetail.error ?? undefined} />
      <div className="mb-10">
        <ResidentHeaderContainer residentDetail={residentDetail} showEditProfile={configurationPermission} />
      </div>
      {!residentError && <TabsPanel tabs={tabs} useHash={true} />}
    </>
  );
};
const connectedResidentDetail = connect((state: State) => ({
  loadResidentDetail: state.residentDetail.loadResident,
  residentLoading: state.residentDetail.loading,
  residentError: state.residentDetail.error,
  residentDetail: state.residentDetail,
}))(ResidentDetail);

export default dashboardWrapper(connectedResidentDetail);
